import { Card } from '@/components/Card';
import { Heading } from '@/components/Heading';
import { SpinnerIcon, DownloadIcon, WarningIcon } from '@/components/Icons';
import { StatusTag } from '@/components/Status';
import { THEME_VARIABLE } from '@/config';
import { File } from '@/types';
import { formatDateByDayjs, isCloseToExpiration } from '@/utils';
import { Divider, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const FileCardItem = ({ file, name, created_at, expire_at }: File) => {
  const { t } = useTranslation();

  const isExpiringSoon = isCloseToExpiration(expire_at);
  const hasExpiration = !!expire_at;

  return (
    <Card
      extraClassName={`mt-2 pt-6 pb-8 rounded-2xl flex gap-3 ${
        isExpiringSoon ? 'border-2 border-customOrange bg-customWarningSecondary' : ''
      }`}
    >
      <Flex justify='space-between'>
        <Typography.Text className='text-base'>{t('bulk_export')}</Typography.Text>
        {file ? (
          <StatusTag
            text={t('completed')}
            color={'green'}
            className='border-2'
            iconColor={THEME_VARIABLE?.customGreen}
          />
        ) : (
          <Flex gap={8}>
            <div className='animate-spin w-6 h-6'>
              <SpinnerIcon />
            </div>
            <Typography.Text className='text-base font-thin text-customGrayShade5'>
              {t('in_queue')}
            </Typography.Text>
          </Flex>
        )}
      </Flex>
      <Divider className='my-0' />
      <Flex justify='space-between'>
        <Typography.Text className='text-base font-thin text-customGrayShade5'>
          {t('requested_date')}
        </Typography.Text>
        <Typography.Text className='text-base font-thin text-customGrayShade5'>
          {formatDateByDayjs(created_at, 'D MMM YYYY, hh:mm')}
        </Typography.Text>
      </Flex>
      <Flex justify='space-between'>
        <Typography.Text className='text-base font-thin text-customGrayShade5'>
          {t('name')}
        </Typography.Text>
        <Typography.Text className='text-base font-thin text-customGrayShade5'>
          {name}
        </Typography.Text>
      </Flex>
      {hasExpiration && (
        <Flex
          vertical
          gap={4}
          className={`p-3 rounded-lg ${isExpiringSoon ? 'bg-white' : 'bg-customWarningSecondary'}`}
        >
          <Flex gap={8} align='center'>
            {isExpiringSoon && <WarningIcon />}
            <Typography.Text className='text-sm font-medium text-customTunaPrimary'>
              {t('file_expiration_warning', {
                date: formatDateByDayjs(expire_at, 'D MMM YYYY, hh:mm A'),
              })}
            </Typography.Text>
          </Flex>
        </Flex>
      )}
      {file && (
        <button
          type='button'
          onClick={() => {
            window.location.href = file.abs_url;
          }}
          className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors mt-4'
        >
          <DownloadIcon />
          <Heading
            title={t('download')}
            fontWeight='font-bold'
            fontSize='text-sm'
            upperCase
            extraClassName='leading-5'
          />
        </button>
      )}
    </Card>
  );
};

export default FileCardItem;
